import type { ItemDto } from '@wishare/shared';
import { isFullyTaken } from './item-state.js';

/**
 * Поиск и отбор желаний внутри списка.
 *
 * Список «хочу когда-нибудь» у активного человека растёт годами и доходит до
 * сотен позиций. Отбор идёт на стороне браузера: желания уже загружены целиком,
 * и гонять запрос на сервер ради подстроки — только добавлять задержку там,
 * где ответ может быть мгновенным.
 */

export type ItemFilter = 'all' | 'free' | 'mine' | 'expensive';

export const FILTER_LABEL: Record<ItemFilter, string> = {
  all: 'Все',
  free: 'Свободные',
  mine: 'Дарю я',
  expensive: 'Дорогие',
};

/** Дорогим считаем то, на что обычно скидываются, а не дарят в одиночку. */
export const EXPENSIVE_FROM_MINOR = 15_000_00;

/**
 * Приводит текст к виду, удобному для сравнения.
 *
 * Кроме регистра снимаем разницу между «ё» и «е»: человек ищет «елку»,
 * а в названии «ёлка» — и не находит ничего, хотя товар прямо перед ним.
 */
function normalize(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е').trim();
}

/** Подходит ли желание под поисковый запрос. */
export function matchesQuery(item: ItemDto, query: string): boolean {
  const needle = normalize(query);
  if (!needle) return true;

  // Ищем по всем полям, где человек мог запомнить вещь: по названию,
  // по описанию, по магазину и по самой ссылке.
  const haystack = normalize(
    [item.title, item.description ?? '', item.sourceSite ?? '', item.url ?? ''].join(' '),
  );

  // Несколько слов — каждое должно найтись, но порядок не важен:
  // «термос сталь» должен находить «Термос из нержавеющей стали».
  return needle.split(/\s+/).every((word) => haystack.includes(word));
}

/** Подходит ли желание под выбранный отбор. */
export function matchesFilter(item: ItemDto, filter: ItemFilter): boolean {
  switch (filter) {
    case 'free':
      // Владельцу поле брони не приходит вовсе — для него «свободны» все.
      if (!item.reservation) return true;
      // Складчина остаётся свободной и с нулевым остатком: в неё можно войти,
      // пока подарок не куплен, — см. isFullyTaken.
      return !isFullyTaken(item) && !item.reservation.mine;

    case 'mine':
      return Boolean(item.reservation?.mine);

    case 'expensive':
      return (item.priceMinor ?? 0) >= EXPENSIVE_FROM_MINOR;

    case 'all':
    default:
      return true;
  }
}

export function filterItems(items: ItemDto[], query: string, filter: ItemFilter): ItemDto[] {
  return items.filter((item) => matchesFilter(item, filter) && matchesQuery(item, query));
}

/**
 * Какие отборы имеет смысл предлагать на этом списке.
 *
 * Показывать «Дарю я» владельцу бессмысленно — он в своём списке не дарит.
 * «Дорогие» без единой дорогой вещи — тоже лишняя кнопка.
 */
export function availableFilters(items: ItemDto[], viewerIsOwner: boolean): ItemFilter[] {
  const filters: ItemFilter[] = ['all'];

  if (!viewerIsOwner && items.some((item) => item.reservation)) {
    filters.push('free');
    if (items.some((item) => item.reservation?.mine)) filters.push('mine');
  }

  if (items.some((item) => (item.priceMinor ?? 0) >= EXPENSIVE_FROM_MINOR)) {
    filters.push('expensive');
  }

  // Один-единственный вариант — это не выбор, а украшение.
  return filters.length > 1 ? filters : [];
}
